import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { Box, TextField, Typography } from "@mui/material";
import { grey } from '@mui/material/colors';

import CodeUtil from "@plannel/pages/utils/code-util";
import p13nService from "@plannel/services/system/p13n-service";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

const MODAL_TITLE = "agedInventorySetting";
// RESET 은 이 모달에 없다(버튼도 핸들러 분기도 없음) — 형제 모달에서 복붙된 흔적이라 뺐다
const DIALOG_TITLE = { SAVE: "SAVE", CLOSE: "CLOSE" };
const ROW_H = 40;              // Chart Settings(MeasureConfigList) 행 규격
const LAST_GRADE = "E";        // 마지막 등급은 상한이 없다 → days 입력 없음

const ThresholdModal = (props) => {
  const { t, open, setOpen, applyThreseHolds, getLabelByGrade, viewName, thresholdCellStyle, thresholdGrade, targetCd, modalTitle } = props;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [p13nConfigObj, setP13nConfigObj] = useState({});
  const [initTresholdData, setInitTresholdData] = useState([]);   // 저장돼 있는 값
  const [draft, setDraft] = useState([]);                          // 편집 중인 값

  const { snackBarMessageColor } = ColorUtil;

  const defaultGrade = [
    { grade: "A", days: 90 },
    { grade: "B", days: 120 },
    { grade: "C", days: 180 },
    { grade: "D", days: 365 },
    { grade: LAST_GRADE, days: null }
  ];

  const defaultCellStyle = {
    A : CodeUtil.ABCClassConfig.AX?.style,
    B : CodeUtil.ABCClassConfig.AY?.style,
    C : CodeUtil.ABCClassConfig.AZ?.style,
    D : CodeUtil.ABCClassConfig.BZ?.style,
    E : CodeUtil.ABCClassConfig.CZ?.style
  }
  const gradeStyle = thresholdCellStyle || defaultCellStyle;

  const handleClose = () => {
    setOpen(false);
  }

  /*
   * 설명문은 저장하지 않는다 — days 에서 매번 계산한다.
   * 앞 등급의 상한 + 1 이 다음 등급의 하한이다(그리드 시절 setGridData/onCellValueChanged 가 하던 계산).
   */
  const describe = (rows, index) => {
    // days 가 비어 있으면 null + 1 = 1 이 된다(JS 강제변환) — 그리드 시절 preEndDays 계산과 같아야 하므로 ?? 를 쓰지 말 것
    const start = index === 0 ? 0 : rows[index - 1].days + 1;
    return getLabelByGrade({ grade: rows[index].grade, start, end: rows[index].days });
  }

  const getThresholdDataLists = () => {
    p13nService.get(viewName)
    .then(response => {
      const { data } = response;
      setP13nConfigObj(data);

      const defaultDataList = thresholdGrade ? thresholdGrade : defaultGrade;
      const thresholdDatas = data[targetCd] ? data[targetCd] : defaultDataList;

      setInitTresholdData(thresholdDatas);
      applyThreseHolds(thresholdDatas);
      })
      .catch(e => {
        debug(e);
      }).then(() => {
        handleClose();
      });
  }

  // 열 때마다 저장값으로 되돌린다 — 취소하고 다시 열면 편집 흔적이 남으면 안 된다
  useEffect(() => {
    if (open) {
      setDraft(initTresholdData.map(row => ({ ...row })));
    }
  }, [open, initTresholdData]);

  const checkThresholdValidation = (thresholdDatas) => {
    if (thresholdDatas.length > 0) {
      for (let i = 0; i < thresholdDatas.length - 1; i++) {
        if (i === 0) {
          if (thresholdDatas[i].days < 0) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${thresholdDatas[i].grade} : ${t("MSG_ValueGreater")} ${0}` });

            return false;
          }
        } else {
          if (thresholdDatas[i-1].days > thresholdDatas[i].days) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${thresholdDatas[i].grade} : ${t("MSG_ValueGreater")} ${thresholdDatas[i-1].days}` });

            return false;
          }
        }
      }
    }
    return true;
  }

  const saveDimensions = () => {
    const thresholdDatas = draft.map(({ grade, days }) => ({ grade, days }));

    if (!checkThresholdValidation(thresholdDatas)) {
      return;
    }

    const configObj = { ...p13nConfigObj };
    configObj[targetCd] = thresholdDatas;

    p13nService.upsert({ pageCd: viewName, configObj })
    .then(() => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
    })
    .catch(e => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      debug(e);
    })
    .then(() => {
      getThresholdDataLists();
    });
  }

  const handleDialogOpen = (action) => {
    if (action === DIALOG_TITLE.SAVE) {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    } else if (action === DIALOG_TITLE.CLOSE) {
      // 그리드 시절엔 DataState 가 변경 여부를 들고 있었다 — 이제 draft 와 저장값을 직접 비교한다
      if (!isEqual(draft, initTresholdData)) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        handleClose();
      }
    }
  }

  const handleDialog = (action, _falseAction) => {
    switch (action) {
      case DIALOG_TITLE.SAVE: {
        saveDimensions();
        break;
      }
      case DIALOG_TITLE.CLOSE: {
        handleClose();
        break;
      }
      default: break;
    }
  }

  const changeDays = (index, value) => {
    setDraft(rows => rows.map((row, i) => (i === index ? { ...row, days: value === "" ? null : Number(value) } : row)));
  }

  useEffect(() => {
    getThresholdDataLists();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerSx = { fontSize: FS.BODY, color: "text.secondary" };

  return <>
    <BaseConfigModal
      open={open}
      onClose={() => handleDialogOpen(DIALOG_TITLE.CLOSE)}
      title={t(modalTitle || MODAL_TITLE)}
      onSave={() => handleDialogOpen(DIALOG_TITLE.SAVE)}
      /*
       * 본문(5행 폼)은 md(360)면 충분하지만 **제목이 2줄로 접힌다**. 20px/700 기준 필요 폭:
       *   en "Aged Inventory Threshold Setting"      32자 ≈ 340px
       *   vi "Thiết Lập Ngưỡng Hàng Lưu Kho Lâu Năm" 37자 ≈ 390px  ← 가장 김
       *   ja/zh/ko                                   11~12자 ≈ 240px
       * 제목 좌우 패딩(16+24) 까지 더하면 vi 가 약 430px → md 도, 400 같은 예외 폭도 부족하다.
       * 언어마다 폭을 재는 대신 프리셋 lg(500)로 간다.
       */
      size="lg">
      {/* 등급 5행 × 3열짜리 폼이다 — 정렬/리사이즈/메뉴가 없어 ag-grid 를 쓸 이유가 없다.
          첫 열은 auto — 고정 폭을 주면 "Threshold" 헤더가 Days 칸을 침범한다(칩보다 헤더가 넓다) */}
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 90px 1fr", gap: 1, alignItems: "center" }}>
        <Typography sx={headerSx}>{t("threshold")}</Typography>
        <Typography sx={headerSx}>{t("days")}</Typography>
        <Typography sx={headerSx}>{t("descTxt")}</Typography>
        {draft.map((row, index) => {
          const isLast = isEqual(row.grade, LAST_GRADE);
          return (
            <Box key={row.grade} sx={{ display: "contents" }}>
              {/* 칩은 40px 로 두고 열은 헤더 폭에 맞춘다 — 칩이 헤더만큼 늘어나면 어색하다 */}
              <Box sx={{ ...gradeStyle[row.grade], width: 40, minHeight: ROW_H - 16, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 0.5, fontSize: FS.TITLE }}>
                {row.grade}
              </Box>
              {/* 마지막 등급은 상한이 없다(days = null) — 그리드에선 회색 칠한 비편집 셀이었다 */}
              <TextField
                size="small"
                type="number"
                value={row.days ?? ""}
                disabled={isLast}
                onChange={e => changeDays(index, e.target.value)}
                sx={{ "& .MuiInputBase-input": { fontSize: FS.TITLE, py: 0.75, textAlign: "right" }, "& .Mui-disabled": { backgroundColor: grey[300] } }}
              />
              <Typography sx={{ fontSize: FS.TITLE, color: "text.primary" }}>{describe(draft, index)}</Typography>
            </Box>
          );
        })}
      </Box>
    </BaseConfigModal>
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
      el={dialogInfo.el}
    />
  </>
}

export default withTranslation()(ThresholdModal);
